import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Task } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Divider, Flex, Stack, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconChevronRight, IconPencil } from "@tabler/icons-react";

import { useLaborEncounterCloseoutGuard } from "@/clients/hooks/useLaborEncounterCloseoutGuard";
import { usePregnancyObservations } from "@/clients/utils/hooks";
import ConfirmationModal from "@/components/ConfirmationModal";
import GAAlertMessage from "@/components/GAAlertMessage/GAAlertMessage";
import { TextWrapper } from "@/components/TextWrapper";
import { ProfileContext } from "@/contexts/profile";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import {
  calculateGestationalAgeInWeeksAndDays,
  fetchPostpartumTrackerForPregnancyListId,
  Gestation,
  getGestationalAgeDisplay,
  isGestationalAgeOverdue,
  postpartumWeeksAndDaysAt,
} from "@/pregnancy";
import { RootState, useAppDispatch } from "@/store";
import { setGTPALValues } from "@/store/slices/gtpal";
import {
  selectCurrentPregnancyRecord,
  selectMostRecentPastPregnancyRecord,
} from "@/store/slices/pregnancy";
import { usDateFormat } from "@/utils/datetime";
import { useTrigger } from "@/utils/hooks/use-trigger";

import ChecklistCloseoutDrawer from "../ObstetricalProfile/ChecklistCloseoutDrawer";
import { EDDDrawer } from "../ObstetricalProfile/EDDDrawer";
import GTPALDrawer from "../ObstetricalProfile/GTPALDrawer";
import PostPartumTracker from "../ObstetricalProfile/PostPartumTracker";
import CloseoutDrawer from "../ObstetricalProfile/PregnancyCloseout/CloseoutDrawer";

import classes from "./ClientPregnancyStatus.module.css";
interface PregnancyStatusCardProps {
  canEdit: boolean;
  isPregnant: boolean | undefined;
  eddDisplay: string;
  gestation: { weeks?: number | string; days?: number | string } | undefined;
  openObstetricalProfileModal: (event: React.MouseEvent<HTMLButtonElement>) => void;
  openEDDModal: () => void;
  onCloseOutPregnancyClick?: () => void;
  livePostpartumAge?: Gestation;
  showCloseOutPregnancyAction?: boolean;
}

const PregnancyStatusCard = ({
  openObstetricalProfileModal: openGTPALModal,
  openEDDModal,
  onCloseOutPregnancyClick,
  canEdit,
  livePostpartumAge,
  isPregnant,
  eddDisplay,
  gestation,
  showCloseOutPregnancyAction = false,
}: PregnancyStatusCardProps) => {
  const { gravida, term, preterm, abortion, living } = useSelector(
    (state: RootState) => state.gtpal,
  );

  const isOver40weeks = isGestationalAgeOverdue(isPregnant, gestation);

  const gestationDisplay =
    isPregnant && gestation
      ? getGestationalAgeDisplay({
          weeks: Number(gestation.weeks ?? 0),
          days: Number(gestation.days ?? 0),
        })
      : "--";

  const gaEDDRowClassName = `${classes.pregnancyStatusRow}${
    isOver40weeks ? ` ${classes.pregnancyStatusRowOverdue}` : ""
  }`;

  const gaEDDRow = (
    <UnstyledButton
      disabled={!canEdit}
      onClick={openEDDModal}
      data-testid="edd-button"
      className={gaEDDRowClassName}
    >
      <Flex gap="xs" wrap="nowrap" align="center" className={classes.pregnancyStatusRowContent}>
        {isOver40weeks && <GAAlertMessage iconSize={20} />}
        <Flex gap={4} wrap="nowrap">
          <TextWrapper m={0} size="xs" variant="bold">
            GA
          </TextWrapper>
          <TextWrapper m={0} size="xs" c="dark.9" variant="bold">
            {gestationDisplay}
          </TextWrapper>
        </Flex>
        <Flex gap={4} wrap="nowrap">
          <TextWrapper m={0} size="xs" variant="bold">
            EDD
          </TextWrapper>
          <TextWrapper m={0} size="xs" c="dark.9" variant="bold">
            {isPregnant ? eddDisplay : "--"}
          </TextWrapper>
        </Flex>
      </Flex>
      {canEdit && (
        <span className={classes.pregnancyStatusRowIcon}>
          <IconPencil size={18} />
        </span>
      )}
    </UnstyledButton>
  );

  return (
    <Stack gap={0} bg="white" className={classes.pregnancyStatusCard}>
      {livePostpartumAge && !isPregnant ? (
        <PostPartumTracker
          canEdit={canEdit}
          onClick={openEDDModal}
          postpartumAge={livePostpartumAge}
          rowClassName={classes.pregnancyStatusRow}
          iconClassName={classes.pregnancyStatusRowIcon}
          contentClassName={classes.pregnancyStatusRowContent}
        />
      ) : (
        gaEDDRow
      )}
      {showCloseOutPregnancyAction && onCloseOutPregnancyClick && (
        <>
          <Divider color="cream.3" />
          <UnstyledButton
            onClick={onCloseOutPregnancyClick}
            className={classes.pregnancyStatusRow}
            data-testid="close-out-pregnancy-button"
          >
            <TextWrapper m={0} size="xs" c="dark.9" variant="bold">
              Close Out Pregnancy
            </TextWrapper>
            <span className={classes.pregnancyStatusRowIcon}>
              <IconChevronRight size={18} />
            </span>
          </UnstyledButton>
        </>
      )}
      <Divider color="cream.3" />
      <UnstyledButton
        disabled={!canEdit}
        onClick={openGTPALModal}
        data-testid="gtpal-button"
        className={classes.pregnancyStatusRow}
      >
        <TextWrapper m={0} size="xs" c="dark.9" variant="bold">
          G{gravida || 0}P{term || 0}
          {preterm || 0}
          {abortion || 0}
          {living || 0}
        </TextWrapper>
        {canEdit && (
          <span className={classes.pregnancyStatusRowIcon}>
            <IconPencil size={18} />
          </span>
        )}
      </UnstyledButton>
    </Stack>
  );
};

export default function ClientPregnancyStatus() {
  const { isPatient } = useContext(ProfileContext);
  const dispatch = useAppDispatch();
  const { clientId } = useParams() as { clientId: string };
  const location = useLocation();
  const navigate = useNavigate();
  const {
    pregnancyData,
    isLoading: isPregnancyObservationsLoading,
    refresh,
  } = usePregnancyObservations({ clientId });
  const [eddRefreshTrigger, triggerEDDRefresh] = useTrigger();
  const [obstetricalProfileRefreshTrigger, triggerObstetricalProfileRefresh] = useTrigger();
  const [postpartumTrackerTask, setPostpartumTrackerTask] = useState<Task | undefined>(undefined);
  const medplum = useMedplum();
  const { hasEncounterSignPermission, hasProviderOrScribeRole, hasWriteRole } =
    usePractitionerPermissions();

  /** EDD and GTPAL are chart data: Clients and Read-Only staff see the values but cannot change them. */
  const canEdit = !isPatient && hasWriteRole;

  const isPregnant = pregnancyData?.pregnant ?? false;

  const activePregnancyRecord = useSelector(selectCurrentPregnancyRecord(clientId));
  const mostRecentPastRecord = useSelector(selectMostRecentPastPregnancyRecord(clientId));

  const eddFromRecord = activePregnancyRecord?.edd;
  const gestationFromRecord = eddFromRecord
    ? calculateGestationalAgeInWeeksAndDays(eddFromRecord)
    : undefined;

  const deliveryDate = mostRecentPastRecord?.deliveryDate;
  const livePostpartumAgeResult = deliveryDate ? postpartumWeeksAndDaysAt(deliveryDate) : undefined;
  const livePostpartumAge = useMemo(
    () =>
      livePostpartumAgeResult
        ? { weeks: livePostpartumAgeResult.weeks, days: livePostpartumAgeResult.days }
        : undefined,
    [livePostpartumAgeResult],
  );

  const effectiveIsPregnant = activePregnancyRecord?.isActive ?? false;
  const effectiveEddDisplay = eddFromRecord ? usDateFormat(eddFromRecord) : "--";
  const effectiveGestation = gestationFromRecord;

  const [
    isObstetricalProfileModalOpened,
    { open: openObstetricalProfileModal, close: closeObstetricalProfileModal },
  ] = useDisclosure(false);
  const [
    isPregnancyCloseoutDrawerOpened,
    { open: openPregnancyCloseoutDrawer, close: closePregnancyCloseoutDrawer },
  ] = useDisclosure(false);

  const handleCloseoutComplete = () => {
    refresh();
    triggerEDDRefresh();
  };

  const handlePregnancyCloseoutDrawerComplete = () => {
    handleCloseoutComplete();
    triggerObstetricalProfileRefresh();
    openObstetricalProfileModal();
  };

  const [
    isChecklistCloseoutDrawerOpened,
    { open: openChecklistCloseoutDrawer, close: closeChecklistCloseoutDrawer },
  ] = useDisclosure(false);

  const [isEDDModalOpened, { open: openEDDModal, close: closeEDDModal }] = useDisclosure(false);

  const handleOpenEDDModal = useCallback(async () => {
    if (!activePregnancyRecord && livePostpartumAge && mostRecentPastRecord?.list.id) {
      const task = await fetchPostpartumTrackerForPregnancyListId(
        medplum,
        mostRecentPastRecord.list.id,
      );
      setPostpartumTrackerTask(task);
    } else {
      setPostpartumTrackerTask(undefined);
    }

    openEDDModal();
  }, [
    activePregnancyRecord,
    livePostpartumAge,
    medplum,
    mostRecentPastRecord?.list.id,
    openEDDModal,
  ]);

  const showCloseOutPregnancyAction = canEdit && hasProviderOrScribeRole && effectiveIsPregnant;

  const {
    closeConfirmation,
    confirmLaborEncounterNavigation,
    confirmationModalProps,
    isConfirmationOpened,
    requestCloseout,
  } = useLaborEncounterCloseoutGuard({
    clientId,
    prefetchEnabled: showCloseOutPregnancyAction,
  });

  const requestPregnancyCloseout = useCallback(async () => {
    const canProceed = await requestCloseout();
    if (canProceed) {
      openPregnancyCloseoutDrawer();
    }
  }, [openPregnancyCloseoutDrawer, requestCloseout]);

  useEffect(() => {
    if (!location.state?.openPregnancyCloseoutDrawer) return;

    const hasActivePregnancyRecord = activePregnancyRecord?.isActive ?? false;
    const isPostpartumClient = !activePregnancyRecord && !!mostRecentPastRecord;

    // Wait until pregnancy status is resolved before consuming navigation state.
    if (
      isPregnancyObservationsLoading ||
      (!hasActivePregnancyRecord && isPregnant && !isPostpartumClient)
    ) {
      return;
    }

    // Sign-and-lock closeout needs sign permission and an active pregnancy, and uses the L&D guard.
    if (hasEncounterSignPermission && hasActivePregnancyRecord) {
      void requestPregnancyCloseout();
    }

    const {
      openPregnancyCloseoutDrawer: _openPregnancyCloseoutDrawer,
      statusChanged: _statusChanged,
      ...remainingState
    } = location.state;
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(remainingState).length > 0 ? remainingState : undefined,
    });
  }, [
    activePregnancyRecord,
    hasEncounterSignPermission,
    isPregnant,
    isPregnancyObservationsLoading,
    location.pathname,
    location.state,
    mostRecentPastRecord,
    navigate,
    requestPregnancyCloseout,
  ]);

  // Clear stale data immediately when clientId changes
  useEffect(() => {
    dispatch(
      setGTPALValues({
        gravida: undefined,
        term: undefined,
        preterm: undefined,
        abortion: undefined,
        living: undefined,
      }),
    );
    setPostpartumTrackerTask(undefined);
  }, [clientId, dispatch]);

  useEffect(() => {
    // Only update store when we have pregnancy data to avoid clearing on re-fetch
    if (!pregnancyData) return;

    const gravida = pregnancyData.gravidity;
    const { term, preterm, abortions, living } = pregnancyData;

    dispatch(setGTPALValues({ gravida, term, preterm, abortion: abortions, living }));
  }, [pregnancyData, dispatch]);

  const handlePostpartumTrackerComplete = useCallback(() => {
    setPostpartumTrackerTask(undefined);
    triggerEDDRefresh();
  }, [triggerEDDRefresh]);

  return (
    <>
      {canEdit && (
        <>
          <EDDDrawer
            onClose={closeEDDModal}
            opened={isEDDModalOpened}
            isCurrentlyPregnant={isPregnant}
            refreshTrigger={eddRefreshTrigger}
            postpartumTrackerTask={postpartumTrackerTask}
            onPostpartumTrackerComplete={handlePostpartumTrackerComplete}
          />
          <GTPALDrawer
            onClose={closeObstetricalProfileModal}
            opened={isObstetricalProfileModalOpened}
            onCloseoutComplete={handleCloseoutComplete}
            refreshTrigger={obstetricalProfileRefreshTrigger}
            openChecklistCloseoutDrawer={openChecklistCloseoutDrawer}
          />
          <PermissionGate mode="hide" requiredPermissions={PROVIDER_SCRIBE_PERMISSION_FLAGS}>
            <CloseoutDrawer
              onClose={closePregnancyCloseoutDrawer}
              opened={isPregnancyCloseoutDrawerOpened}
              trigger={handlePregnancyCloseoutDrawerComplete}
            />
          </PermissionGate>
          <ChecklistCloseoutDrawer
            onClose={closeChecklistCloseoutDrawer}
            opened={isChecklistCloseoutDrawerOpened}
          />
          <ConfirmationModal
            isDeleting={false}
            {...confirmationModalProps}
            onClose={closeConfirmation}
            opened={isConfirmationOpened}
            onConfirm={() => confirmLaborEncounterNavigation()}
          />
        </>
      )}
      <PregnancyStatusCard
        canEdit={canEdit}
        gestation={effectiveGestation}
        isPregnant={effectiveIsPregnant}
        eddDisplay={effectiveEddDisplay}
        openEDDModal={handleOpenEDDModal}
        livePostpartumAge={livePostpartumAge}
        openObstetricalProfileModal={openObstetricalProfileModal}
        showCloseOutPregnancyAction={showCloseOutPregnancyAction}
        onCloseOutPregnancyClick={() => {
          void requestPregnancyCloseout();
        }}
      />
    </>
  );
}
